"use client";

import { useMemo } from "react";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import { getCheckoutGeoPricingCountryCode } from "./getCheckoutGeoPricingCountryCode";
import { useMonarchRule } from "@/monarch/rules/MonarchRulesProvider";
import { CHECKOUT_GEO_PRICING_RULE_ID } from "@/monarch/rules/checkoutGeoPricing/types";
import { getCheckoutGeoPricingDiscountByCountry } from "./getCheckoutGeoPricingDiscountByCountry";
import type {
  CheckoutGeoPricingEligibility,
  CheckoutGeoPricingTestData,
  UseCheckoutGeoPricingEligibilityParams,
} from "./types";

const ineligible = (isLoading: boolean): CheckoutGeoPricingEligibility => ({
  eligible: false,
  isLoading,
});

type ResolveCheckoutGeoPricingEligibilityParams = {
  flagValue: string | null;
  flagLoading: boolean;
  monarchLoading: boolean;
  monarchData?: CheckoutGeoPricingTestData;
  countryCode: string | null;
  product?: UseCheckoutGeoPricingEligibilityParams["product"];
};

export const resolveCheckoutGeoPricingEligibility = ({
  flagValue,
  flagLoading,
  monarchLoading,
  monarchData,
  countryCode,
  product,
}: ResolveCheckoutGeoPricingEligibilityParams): CheckoutGeoPricingEligibility => {
  if (flagLoading || flagValue === null) {
    return ineligible(true);
  }

  if (flagValue !== "test") {
    return ineligible(false);
  }

  if (monarchLoading) {
    return ineligible(true);
  }

  if (!monarchData?.isGeoPricingEnabled) {
    return ineligible(false);
  }

  if (!countryCode) {
    return ineligible(false);
  }

  const discount = getCheckoutGeoPricingDiscountByCountry({
    data: monarchData,
    country: countryCode,
    product,
  });

  if (!discount) {
    return ineligible(false);
  }

  return {
    eligible: true,
    discountPercentage: discount.discountPercentage,
    countryCode,
    discountCode: discount.discountCode,
    isLoading: false,
  };
};

/**
 * Resolves checkout geo-pricing eligibility for PDP/CYT/SCP using checkout-parity rules.
 * Gated by `NEXT_PUBLIC_FEATURE_GEO_DYNAMIC_PRICING` and Monarch `checkoutGeoPricingTest`.
 */
export function useCheckoutGeoPricingEligibility({
  product,
  enabled = true,
}: UseCheckoutGeoPricingEligibilityParams = {}): CheckoutGeoPricingEligibility {
  const { flagValue, isLoading: flagLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_GEO_DYNAMIC_PRICING",
    "legacy",
  );

  const monarchEnabled =
    enabled && !flagLoading && flagValue !== null && flagValue === "test";

  const { isLoading: monarchLoading, value } = useMonarchRule<{
    data: CheckoutGeoPricingTestData;
  }>(CHECKOUT_GEO_PRICING_RULE_ID, {}, { enabled: monarchEnabled });

  const countryCode = getCheckoutGeoPricingCountryCode();
  return useMemo(() => {
    if (!enabled) {
      return ineligible(false);
    }

    return resolveCheckoutGeoPricingEligibility({
      flagValue,
      flagLoading,
      monarchLoading,
      monarchData: value?.data,
      countryCode,
      product,
    });
  }, [
    enabled,
    flagValue,
    flagLoading,
    monarchLoading,
    value?.data,
    countryCode,
    product,
  ]);
}
